import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Settings, 
  Users, 
  List, 
  BarChart3, 
  Plus, 
  Trash2, 
  RefreshCw,
  Loader2,
  AlertCircle,
  CheckCircle,
  X,
  User,
  Mail,
  Shield,
  Tv,
  Heart,
  Globe
} from 'lucide-react';
import Navigation from '../components/Navigation';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('statistics');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal states
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showAddPlaylistModal, setShowAddPlaylistModal] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'user' });
  const [newPlaylist, setNewPlaylist] = useState({ name: '', url: '', auto_refresh: false });
  const [submitting, setSubmitting] = useState(false);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/users`, {
        withCredentials: true
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchPlaylists = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/playlists`, {
        withCredentials: true
      });
      setPlaylists(response.data);
    } catch (error) {
      console.error('Error fetching playlists:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchUsers(), fetchPlaylists()]);
      setLoading(false);
    };
    fetchData();
  }, []);

  const handleAddUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/admin/users`, newUser, {
        withCredentials: true
      });
      setSuccess('User created successfully');
      setShowAddUserModal(false);
      setNewUser({ name: '', email: '', password: '', role: 'user' });
      fetchUsers();
      fetchStats();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}`, {
        withCredentials: true
      });
      setSuccess('User deleted successfully');
      fetchUsers();
      fetchStats();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleAddPlaylist = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/admin/playlists`, newPlaylist, {
        withCredentials: true
      });
      setSuccess('Playlist added successfully');
      setShowAddPlaylistModal(false);
      setNewPlaylist({ name: '', url: '', auto_refresh: false });
      fetchPlaylists();
      fetchStats();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add playlist');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRefreshPlaylist = async (playlistId) => {
    try {
      await axios.post(`${API_URL}/api/admin/playlists/${playlistId}/refresh`, {}, {
        withCredentials: true
      });
      setSuccess('Playlist refreshed successfully');
      fetchPlaylists();
      fetchStats();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to refresh playlist');
    }
  };

  const handleDeletePlaylist = async (playlistId) => {
    if (!window.confirm('Are you sure you want to delete this playlist? All associated channels will be removed.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/playlists/${playlistId}`, {
        withCredentials: true
      });
      setSuccess('Playlist deleted successfully');
      fetchPlaylists();
      fetchStats();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete playlist');
    }
  };

  const tabs = [
    { id: 'statistics', label: 'Statistics', icon: BarChart3 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'playlists', label: 'Playlists', icon: List },
  ];

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess('');
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navigation />
      
      <main className="pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex items-center gap-3 mb-8">
            <Settings className="w-8 h-8 text-[#E50914]" />
            <h1 className="text-3xl font-bold font-['Outfit']">Admin Dashboard</h1>
          </div>

          {/* Notifications */}
          {error && (
            <div className="flex items-center gap-3 p-4 bg-[#E50914]/10 border border-[#E50914]/30 rounded-lg mb-6 animate-fade-in" data-testid="admin-error">
              <AlertCircle className="w-5 h-5 text-[#E50914] flex-shrink-0" />
              <p className="text-[#E50914] text-sm">{error}</p>
              <button onClick={() => setError('')} className="ml-auto text-[#E50914] hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {success && (
            <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/30 rounded-lg mb-6 animate-fade-in" data-testid="admin-success">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <p className="text-green-500 text-sm">{success}</p>
              <button onClick={() => setSuccess('')} className="ml-auto text-green-500 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 mb-8 border-b border-[#262626] pb-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  activeTab === tab.id
                    ? 'bg-[#E50914] text-white'
                    : 'text-[#A3A3A3] hover:text-white hover:bg-[#1A1A1A]'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-sm font-medium">{tab.label}</span>
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-10 h-10 text-[#E50914] animate-spin" />
            </div>
          ) : (
            <>
              {/* Statistics Tab */}
              {activeTab === 'statistics' && stats && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="stats-grid">
                  <StatCard icon={Users} label="Total Users" value={stats.total_users} color="#E50914" />
                  <StatCard icon={List} label="Playlists" value={stats.total_playlists} color="#3B82F6" />
                  <StatCard icon={Tv} label="Channels" value={stats.total_channels} color="#10B981" />
                  <StatCard icon={Heart} label="Favorites" value={stats.total_favorites} color="#F59E0B" />
                </div>
              )}

              {/* Users Tab */}
              {activeTab === 'users' && (
                <div data-testid="users-section">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold font-['Outfit']">Manage Users</h2>
                    <button
                      onClick={() => setShowAddUserModal(true)}
                      className="btn-primary px-4 py-2 flex items-center gap-2"
                      data-testid="add-user-btn"
                    >
                      <Plus className="w-4 h-4" />
                      Add User
                    </button>
                  </div>
                  <div className="bg-[#121212] border border-[#262626] rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-[#262626]">
                          <th className="text-left px-6 py-4 text-xs tracking-[0.2em] uppercase text-[#A3A3A3] font-semibold">User</th>
                          <th className="text-left px-6 py-4 text-xs tracking-[0.2em] uppercase text-[#A3A3A3] font-semibold">Role</th>
                          <th className="text-left px-6 py-4 text-xs tracking-[0.2em] uppercase text-[#A3A3A3] font-semibold">Created</th>
                          <th className="text-right px-6 py-4 text-xs tracking-[0.2em] uppercase text-[#A3A3A3] font-semibold">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((u) => (
                          <tr key={u.id} className="border-b border-[#262626] last:border-0 hover:bg-[#1A1A1A] transition-colors">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-[#262626] rounded-full flex items-center justify-center">
                                  <User className="w-5 h-5 text-[#A3A3A3]" />
                                </div>
                                <div>
                                  <p className="text-white font-medium">{u.name}</p>
                                  <p className="text-[#A3A3A3] text-sm">{u.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                u.role === 'admin' ? 'bg-[#E50914]/20 text-[#E50914]' : 'bg-[#262626] text-[#A3A3A3]'
                              }`}>
                                {u.role}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-[#A3A3A3] text-sm">
                              {new Date(u.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 text-right">
                              {u.id !== user?.id && (
                                <button
                                  onClick={() => handleDeleteUser(u.id)}
                                  className="p-2 text-[#A3A3A3] hover:text-[#E50914] hover:bg-[#E50914]/10 rounded-lg transition-colors"
                                  data-testid={`delete-user-${u.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Playlists Tab */}
              {activeTab === 'playlists' && (
                <div data-testid="playlists-section">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold font-['Outfit']">Manage Playlists</h2>
                    <button
                      onClick={() => setShowAddPlaylistModal(true)}
                      className="btn-primary px-4 py-2 flex items-center gap-2"
                      data-testid="add-playlist-btn"
                    >
                      <Plus className="w-4 h-4" />
                      Add Playlist
                    </button>
                  </div>
                  {playlists.length === 0 ? (
                    <div className="bg-[#121212] border border-[#262626] rounded-lg p-12 text-center">
                      <Globe className="w-12 h-12 text-[#262626] mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2 font-['Outfit']">No Playlists</h3>
                      <p className="text-[#A3A3A3] mb-4">Add an M3U playlist URL to get started</p>
                      <p className="text-[#A3A3A3] text-sm">
                        Try: <code className="bg-[#1A1A1A] px-2 py-1 rounded text-[#E50914]">https://iptv-org.github.io/iptv/index.m3u</code>
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {playlists.map((playlist) => (
                        <div key={playlist.id} className="bg-[#121212] border border-[#262626] rounded-lg p-6 hover:border-[#E50914]/30 transition-colors">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="text-white font-semibold text-lg">{playlist.name}</h3>
                              <p className="text-[#A3A3A3] text-sm mt-1 break-all">{playlist.url}</p>
                              <div className="flex items-center gap-4 mt-3 text-sm text-[#A3A3A3]">
                                <span className="flex items-center gap-1">
                                  <Tv className="w-4 h-4" />
                                  {playlist.channel_count} channels
                                </span>
                                <span>Updated: {new Date(playlist.last_updated).toLocaleDateString()}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleRefreshPlaylist(playlist.id)}
                                className="p-2 text-[#A3A3A3] hover:text-white hover:bg-[#1A1A1A] rounded-lg transition-colors"
                                title="Refresh playlist"
                                data-testid={`refresh-playlist-${playlist.id}`}
                              >
                                <RefreshCw className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeletePlaylist(playlist.id)}
                                className="p-2 text-[#A3A3A3] hover:text-[#E50914] hover:bg-[#E50914]/10 rounded-lg transition-colors"
                                title="Delete playlist"
                                data-testid={`delete-playlist-${playlist.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Add User Modal */}
      {showAddUserModal && (
        <Modal onClose={() => setShowAddUserModal(false)} title="Add New User">
          <form onSubmit={handleAddUser} className="space-y-4" data-testid="add-user-form">
            <div>
              <label className="block text-xs tracking-[0.2em] uppercase text-[#A3A3A3] font-semibold mb-2">Name</label>
              <input
                type="text"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                className="input-field w-full px-4 py-3"
                required
                data-testid="new-user-name"
              />
            </div>
            <div>
              <label className="block text-xs tracking-[0.2em] uppercase text-[#A3A3A3] font-semibold mb-2">Email</label>
              <input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                className="input-field w-full px-4 py-3"
                required
                data-testid="new-user-email"
              />
            </div>
            <div>
              <label className="block text-xs tracking-[0.2em] uppercase text-[#A3A3A3] font-semibold mb-2">Password</label>
              <input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                className="input-field w-full px-4 py-3"
                required
                data-testid="new-user-password"
              />
            </div>
            <div>
              <label className="block text-xs tracking-[0.2em] uppercase text-[#A3A3A3] font-semibold mb-2">Role</label>
              <select
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                className="input-field w-full px-4 py-3"
                data-testid="new-user-role"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <button type="button" onClick={() => setShowAddUserModal(false)} className="btn-secondary px-4 py-2">
                Cancel
              </button>
              <button type="submit" disabled={submitting} className="btn-primary px-4 py-2 flex items-center gap-2" data-testid="submit-user-btn">
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Create User
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Add Playlist Modal */}
      {showAddPlaylistModal && (
        <Modal onClose={() => setShowAddPlaylistModal(false)} title="Add M3U Playlist">
          <form onSubmit={handleAddPlaylist} className="space-y-4" data-testid="add-playlist-form">
            <div>
              <label className="block text-xs tracking-[0.2em] uppercase text-[#A3A3A3] font-semibold mb-2">Playlist Name</label>
              <input
                type="text"
                value={newPlaylist.name}
                onChange={(e) => setNewPlaylist({ ...newPlaylist, name: e.target.value })}
                className="input-field w-full px-4 py-3"
                placeholder="e.g., Free IPTV"
                required
                data-testid="new-playlist-name"
              />
            </div>
            <div>
              <label className="block text-xs tracking-[0.2em] uppercase text-[#A3A3A3] font-semibold mb-2">M3U URL</label>
              <input
                type="url"
                value={newPlaylist.url}
                onChange={(e) => setNewPlaylist({ ...newPlaylist, url: e.target.value })}
                className="input-field w-full px-4 py-3"
                placeholder="https://example.com/playlist.m3u"
                required
                data-testid="new-playlist-url"
              />
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="auto_refresh"
                checked={newPlaylist.auto_refresh}
                onChange={(e) => setNewPlaylist({ ...newPlaylist, auto_refresh: e.target.checked })}
                className="w-4 h-4 accent-[#E50914]"
                data-testid="new-playlist-auto-refresh"
              />
              <label htmlFor="auto_refresh" className="text-sm text-[#A3A3A3]">Auto-refresh playlist daily</label>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <button type="button" onClick={() => setShowAddPlaylistModal(false)} className="btn-secondary px-4 py-2">
                Cancel
              </button>
              <button type="submit" disabled={submitting} className="btn-primary px-4 py-2 flex items-center gap-2" data-testid="submit-playlist-btn">
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Add Playlist
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-[#121212] border border-[#262626] rounded-lg p-6 hover:border-[#E50914]/20 transition-colors">
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
        <Icon className="w-6 h-6" style={{ color }} strokeWidth={1.5} />
      </div>
      <div>
        <p className="text-[#A3A3A3] text-sm">{label}</p>
        <p className="text-2xl font-bold font-['Outfit'] text-white">{value}</p>
      </div>
    </div>
  </div>
);

// Modal Component
const Modal = ({ onClose, title, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
    <div className="bg-[#121212] border border-[#262626] rounded-2xl w-full max-w-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold font-['Outfit']">{title}</h2>
        <button onClick={onClose} className="p-2 text-[#A3A3A3] hover:text-white hover:bg-[#1A1A1A] rounded-lg transition-colors">
          <X className="w-5 h-5" />
        </button>
      </div>
      {children}
    </div>
  </div>
);

export default AdminPage;
